import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@common/components/Button';
import Icon from '@common/components/Icon';
import { AlertDialog } from '@common/index';
import { X } from 'lucide-react';
import { useState } from 'react';
import styles from './GoogleAds.module.scss';
import GoogleAdsAccounts from './GoogleAdsAccounts';
import cardStyles from './IntegrationCard.module.scss';

interface GoogleAdsIntegrationProps {
    connectedAccount?: any;
    onConnect?: () => void;
    onDisconnect?: () => void;
}

export default function GoogleAdsIntegration({
    connectedAccount, onConnect, onDisconnect 
}: GoogleAdsIntegrationProps) {
    const { nangoIntegrations } = useAuth();
    const {
        googleAds, connectGoogleAds, disconnectGoogleAds 
    } = nangoIntegrations;
    const {
        connected, availableCustomers, hasFetchedAccounts, accountsLoading
    } = googleAds;
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleConnect = async () => {
        setError('');
        setLoading(true);
        try {
            await connectGoogleAds();
            if (typeof onConnect === 'function') {
                onConnect();
            }
        } catch (err: any) {
            setError(err.message || 'Connection failed');
        } finally {
            setLoading(false);
        }
    };

    const handleDisconnect = async () => {
        setError('');
        setLoading(true);
        try {
            await disconnectGoogleAds();
            if (typeof onDisconnect === 'function') {
                onDisconnect();
            }
        } catch (err: any) {
            setError(`Disconnection failed: ${err.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDismissError = () => {
        setError('');
    };

    return (
        <div className={cardStyles.integrationCard}>
            <div className={cardStyles.cardHeader}>
                <Icon name="google" size={24} />
                <h3 className={cardStyles.cardTitle}>Google Ads</h3>
            </div>

            <p className={cardStyles.cardDescription}>
                Connect your Google Ads account to sync campaign performance data,
                track advertising metrics, and optimize your marketing strategy directly
                within Bustly.
            </p>

            {error && !connected ? (
                <div className={styles.errorContainer}>
                    <p className={styles.errorText}>{error}</p>
                    <button
                        onClick={handleDismissError}
                        className={styles.dismissButton}
                        title="Dismiss error"
                    >
                        <X size={14} />
                    </button>
                </div>
            ) : null}

            {connected ? (
                <div className={styles.connectedSection}>
                    <div className={styles.connectedHeader}>
                        {/* Google Ads Accounts Section */}
                        <div className={styles.accountTitle}>
                            Google Ads Account
                            <span className={cardStyles.connectedBadge}>
                                <b style={{ fontSize: 20, marginRight: 4 }}>·</b>
                                Connected
                            </span>
                        </div>
                        <AlertDialog
                            title="Before you disconnect…"
                            subtitle="If you disconnect, Bustly will stop syncing your Google Ads data.
                            Real-time insights will pause. You can reconnect anytime.
                            This won't delete past data unless you choose to remove it."
                            confirmText="Disconnect Google Ads"
                            onConfirm={() => handleDisconnect()}
                            cancelText="Keep Connected"
                        >
                            <Button
                                className={styles.disconnectButton}
                                size="2"
                                variant="outline"
                                color="gray"
                                style={{ padding: '0px 40px' }}
                                loading={loading}
                            >
                                Disconnect
                            </Button>
                        </AlertDialog>
                    </div>
                    <div className={styles.accountsSection}>
                        <GoogleAdsAccounts
                            availableCustomers={availableCustomers}
                            accountsLoading={accountsLoading}
                            hasFetchedAccounts={hasFetchedAccounts}
                        />
                    </div>
                </div>
            ) : (
                <div className={cardStyles.cardActions}>
                    <Button
                        className={cardStyles.connectButton}
                        onClick={() => handleConnect()}
                        size="2"
                        variant="solid"
                        color="primary"
                        style={{ padding: '0px 40px' }}
                        loading={loading}
                        disabled={loading}
                    >
                        {loading ? 'Connecting...' : 'Connect'}
                    </Button>
                </div>
            )}
        </div>
    );
}
